import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Edit, Mail } from 'lucide-react';
import { useTrainingCourses } from '../../../hooks/useTrainingCourses';
import { Modal } from '../../ui/Modal';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { DetailField } from '../course/DetailField';
import { PracticeHistory } from './PracticeHistory';
import { practiceDeliveryModes } from '../../../data/practiceOptions';
import { formatEuro } from '../../../utils/currency';
import { formatCourseDate, productLabel } from '../../../utils/trainingCourses';
import { billingStepLabel, isInPresence, practiceMembers, requesterKindLabel, requesterName, practiceTotal, rateTypeLabel } from '../../../utils/practices';
import { Practice } from '../../../types/practice';
interface PracticeDetailModalProps {
  practice: Practice | null;
  onClose: () => void;
  onEdit: (practice: Practice) => void;
  onEmail: (practice: Practice) => void;
}
export const practiceStatusVariant = {
  Positivo: 'success',
  'In valutazione': 'warning',
  Negativo: 'danger'
} as const;
export function PracticeDetailModal({
  practice,
  onClose,
  onEdit,
  onEmail
}: PracticeDetailModalProps) {
  const sectionTitle = 'text-sm font-semibold text-seform-navy mb-4';
  const total = practice ? practiceTotal(practice) : undefined;
  const courses = useTrainingCourses();
  const assignedCourse = practice?.courseCode ? courses.find(c => c.courseCode === practice.courseCode) : undefined;
  return <Modal isOpen={Boolean(practice)} onClose={onClose} title="Dettaglio pratica" size="2xl" data-id="element-1529" data-mp="3iuxrcp5dd5zf">
      {practice && <div className="space-y-6" data-id="element-1530" data-mp="39sg4q9lsybbw">
          <div className="flex items-start justify-between gap-4 pb-5 border-b border-slate-100" data-id="element-1531" data-mp="39thhciigv5vq">
            <div data-id="element-1532" data-mp="133jp2umci8sn">
              <h3 className="text-xl font-bold text-seform-navy" data-id="element-1533" data-mp="1331jv8hpgk8l">
                {requesterName(practice)}
              </h3>
              <p className="text-sm text-slate-500 mt-0.5" data-id="element-1534" data-mp="17yyxh71l3cho">
                {requesterKindLabel(practice)}
              </p>
            </div>
            <Badge variant={practiceStatusVariant[practice.status]} data-id="element-1535" data-mp="11k1kagalcxmd">
              {practice.status}
            </Badge>
          </div>

          {practice.clientType !== 'individuo' && <section data-id="element-1536" data-mp="rnos5mup2rtq">
              <h4 className={sectionTitle} data-id="element-1537" data-mp="ro6u9041g09k">
                Partecipanti al corso · {practice.attendees?.length || 0}
              </h4>
              {practice.attendees && practice.attendees.length > 0 ? <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4" data-id="element-1538" data-mp="1kc2ntvyb9c24">
                  {practiceMembers(practice).map(m => <li key={m.id} className="min-w-0 min-h-[2.5rem]" data-id="element-1539" data-mp="1am11p9piq3pu">
                      <p className={`text-sm truncate ${m.placeholder ? 'text-amber-700 italic' : 'font-medium text-slate-800'}`} data-id="element-1540" data-mp="1ajolb0zes1ew">
                        {m.name}
                      </p>
                      {m.email && <p className="text-xs text-slate-500 truncate" data-id="element-1541" data-mp="2bknhgahf5p86">{m.email}</p>}
                    </li>)}
                </ul> : <p className="text-sm text-slate-500" data-id="element-1542" data-mp="2kizht48381dt">Nessun partecipante indicato.</p>}
            </section>}

          <section data-id="element-1543" data-mp="2pgraw1kgxtjl">
            <h4 className={sectionTitle} data-id="element-1544" data-mp="2ui6tlmz6bnyt">Informazioni corso</h4>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4" data-id="element-1545" data-mp="2uhopk469s5gc">
              <DetailField label="Macrocategoria" data-id="element-1546" data-mp="2yi7hrzc2rdj2">{practice.category}</DetailField>
              <DetailField label="Codice prodotto + Tipologia corso" data-id="element-1547" data-mp="339z2vucgor1t">
                {productLabel(practice.productCode)}
              </DetailField>
              <DetailField label="Corso richiesto" data-id="element-1548" data-mp="38b67xln7zc4s">
                {practice.requestedCourseCode && <span className="font-mono" data-id="element-1549" data-mp="3d6lju81o2xyk">{practice.requestedCourseCode}</span>}
              </DetailField>
              <DetailField label="Corso assegnato" data-id="element-1550" data-mp="isyxruq7l1nq">
                {assignedCourse ? <Link to={`/training/corsi/${assignedCourse.id}`} className="inline-flex items-center gap-1 text-seform-orange hover:underline" data-id="element-1551" data-mp="dup11fou5t00">
                    <span className="font-mono" data-id="element-1552" data-mp="njp69gtxlkmn">{assignedCourse.courseCode}</span> ·{' '}
                    {assignedCourse.name}
                    <ArrowUpRight className="h-3.5 w-3.5" data-id="element-1553" data-mp="3w0tprt9vvztr" />
                  </Link> : practice.courseCode && <span className="font-mono" data-id="element-1554" data-mp="8vcekw1vmnxy">{practice.courseCode}</span>}
              </DetailField>
              <DetailField label="Tariffa" data-id="element-1555" data-mp="4soat2s0s77u">{rateTypeLabel(practice)}</DetailField>
              {practice.rateType === 'oraria' ? <>
                  <DetailField label="Tariffa oraria" data-id="element-1556" data-mp="1g8wcl1zmneig">
                    {practice.hourlyRate !== undefined && `${formatEuro(practice.hourlyRate)}/h`}
                  </DetailField>
                  <DetailField label="Monte ore corso" data-id="element-1557" data-mp="1sr8itsdymd4e">
                    {practice.courseHours !== undefined && `${practice.courseHours} ore`}
                  </DetailField>
                </> : <>
                  <DetailField label="Costo singola iscrizione" data-id="element-1558" data-mp="1itknb08j51l6">
                    {practice.singleFee !== undefined && formatEuro(practice.singleFee)}
                  </DetailField>
                  <DetailField label="Numero corsisti" data-id="element-1559" data-mp="2bk2mvra1oktu">
                    {practice.participantsCount}
                  </DetailField>
                </>}
              <DetailField label="Importo complessivo senza IVA" data-id="element-1560" data-mp="21qvk8e6ovixg">
                {total !== undefined && formatEuro(total)}
              </DetailField>
              <DetailField label="Email cliente" data-id="element-1561" data-mp="2u8hdwekd3yun">{practice.clientEmail}</DetailField>
              <DetailField label="Provenienza" data-id="element-1562" data-mp="2uac0dxba7z0u">{practice.source}</DetailField>
              <DetailField label="Data" data-id="element-1563" data-mp="343om6k2e7pt9">{formatCourseDate(practice.date)}</DetailField>
              {practice.status === 'Negativo' && <>
                  <DetailField label="Data della non accettazione" data-id="element-1564" data-mp="2v3085pd01pgd">
                    {formatCourseDate(practice.rejectionDate)}
                  </DetailField>
                  <DetailField label="Motivazione della non accettazione" full data-id="element-1565" data-mp="2q719n8xyir28">
                    {practice.rejectionReason}
                  </DetailField>
                </>}
            </dl>
          </section>

          {practice.status === 'Positivo' && <>
              <section className="pt-6 border-t border-slate-100" data-id="element-1566" data-mp="2bdfzcewwwh8c">
                <h4 className={sectionTitle} data-id="element-1567" data-mp="27fs2h6bnsedt">Programmazione corso</h4>
                {practice.deliveryMode ? <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4" data-id="element-1568" data-mp="22eceb5e6prr7">
                    <DetailField label="Tipo di corso" full data-id="element-1569" data-mp="1xgjceojwzjwq">
                      {practiceDeliveryModes.find(m => m.value === practice.deliveryMode)?.label}
                    </DetailField>
                    <DetailField label="Codice Corso" data-id="element-1570" data-mp="mxhg74uva87w">
                      <span className="font-mono" data-id="element-1571" data-mp="qxhw6txm6rh2">{practice.courseCode}</span>
                    </DetailField>
                    <DetailField label="Stato del corso" data-id="element-1572" data-mp="3qdespkgi3s9e">
                      {practice.courseStatus}
                    </DetailField>
                    {isInPresence(practice) ? <>
                        <DetailField label="Data inizio" data-id="element-1573" data-mp="374d3sx0c2ym">
                          {formatCourseDate(practice.courseStartDate)}
                        </DetailField>
                        <DetailField label="Data fine" data-id="element-1574" data-mp="37mndi29u17n3">
                          {formatCourseDate(practice.courseEndDate)}
                        </DetailField>
                        <DetailField label="Dati del corso" full data-id="element-1575" data-mp="3bm6155t1fwtm">
                          {practice.courseDetails}
                        </DetailField>
                      </> : <DetailField label="Partecipanti" full data-id="element-1576" data-mp="203oag1ji5bdq">
                        {practice.participants.length > 0 && <ul className="space-y-1" data-id="element-1577" data-mp="2a011ernnxb4s">
                            {practice.participants.map(p => <li key={p.id} data-id="element-1578" data-mp="2nyqzoam7xguq">
                                {p.firstName} {p.lastName}
                                <span className="text-slate-500 font-normal" data-id="element-1579" data-mp="2j17ib1u84nyk">
                                  {' '}
                                  · {p.email} ·{' '}
                                  {p.credentialsSentAt ? 'credenziali inviate' : 'credenziali da inviare'}
                                </span>
                              </li>)}
                          </ul>}
                      </DetailField>}
                    <DetailField label="Programmazione" data-id="element-1580" data-mp="3qbtpnyjepktl">
                      {practice.schedulingConfirmed ? 'Confermata' : 'Da confermare'}
                    </DetailField>
                  </dl> : <p className="text-sm text-slate-500" data-id="element-1581" data-mp="3ham3geu60afv">
                    Corso non ancora programmato.
                  </p>}
              </section>
              <section className="pt-6 border-t border-slate-100" data-id="element-1582" data-mp="32kejtvsf8ldl">
                <dl className="grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-4" data-id="element-1583" data-mp="37eiqsf1osteo">
                  <DetailField label="Proforma" data-id="element-1584" data-mp="2tmktd1hc8lyc">
                    {billingStepLabel(practice.proforma, 'Proformato')}
                  </DetailField>
                  <DetailField label="Fattura" data-id="element-1585" data-mp="2jndcw5fb4b70">
                    {billingStepLabel(practice.invoice, 'Fatturato')}
                  </DetailField>
                  <DetailField label="Attestati" data-id="element-1586" data-mp="2ol55l1ns01c9">{practice.certificates}</DetailField>
                </dl>
              </section>
            </>}

          <section className="pt-6 border-t border-slate-100" data-id="element-1587" data-mp="3ltvov3vs796e">
            <h4 className={sectionTitle} data-id="element-1588" data-mp="3lv6xbh06953u">Storico e azioni</h4>
            <PracticeHistory history={practice.history} data-id="element-1589" data-mp="37uw5obr1fmhm" />
          </section>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-1590" data-mp="3csxjoi9lwvm8">
            <Button variant="flat" color="default" onClick={onClose} data-id="element-1591" data-mp="4ddwo0zj49fm">
              Chiudi
            </Button>
            <Button variant="flat" color="secondary" startContent={<Mail className="h-4 w-4" data-id="element-1593" data-mp="3qt8e1va4ksc5" />} onClick={() => onEmail(practice)} data-id="element-1592" data-mp="9c7cw167aejh">
              Invia email
            </Button>
            <Button startContent={<Edit className="h-4 w-4" data-id="element-1595" data-mp="3vpp4iw4srise" />} onClick={() => onEdit(practice)} data-id="element-1594" data-mp="3vqgxdz70f2ez">
              Modifica
            </Button>
          </div>
        </div>}
    </Modal>;
}